import React, { useState, useRef } from 'react';
import { Music, RefreshCw, Download, Check, ArrowLeft } from 'lucide-react';

const MusicGenerator = ({ onBack }) => {
  // API URL for your Cloudflare Worker
  const API_URL = 'https://minimax-worker.kgu-ishizaka.workers.dev'; // ここをあなたのCloudflare WorkerのURLに変更してください
  
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(30);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedMusic, setGeneratedMusic] = useState(null);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  const generateMusic = async () => {
    if (!prompt.trim()) {
      setError('音楽の説明を入力してください');
      return;
    }

    setIsGenerating(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/generate-music`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt,
          duration: parseInt(duration),
          temperature: 0.8,
          top_k: 50,
          top_p: 0.8,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `エラー: ${response.status}`);
      }

      const data = await response.json();
      setGeneratedMusic(data);
      
      // 音楽が生成されたら自動再生
      if (audioRef.current) {
        audioRef.current.src = data.music_url;
        audioRef.current.play();
      }
    } catch (err) {
      console.error('Error generating music:', err);
      setError(err.message || '音楽の生成中にエラーが発生しました');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDurationChange = (e) => {
    const value = parseInt(e.target.value);
    setDuration(value);
  };

  const downloadMusic = () => {
    if (generatedMusic && generatedMusic.music_url) {
      const a = document.createElement('a');
      a.href = generatedMusic.music_url;
      a.download = `generated-music-${Date.now()}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <div className="p-4 max-w-2xl mx-auto">
      <div className="flex items-center mb-6">
        <button 
          onClick={onBack} 
          className="mr-3 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-xl font-bold">BGM作成</h1>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            音楽の説明
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="例: エネルギッシュなポップミュージック、ピアノとギターが特徴的"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            rows={4}
          />
          <p className="mt-1 text-xs text-gray-500">
            具体的な説明を入力すると、イメージに近い音楽が生成されます
          </p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            音楽の長さ: {duration}秒
          </label>
          <input
            type="range"
            min="10"
            max="60"
            step="5"
            value={duration}
            onChange={handleDurationChange}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>10秒</span>
            <span>60秒</span>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-md mb-4 text-sm">
            {error}
          </div>
        )}

        <button
          onClick={generateMusic}
          disabled={isGenerating}
          className={`w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-white font-medium ${
            isGenerating ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {isGenerating ? (
            <>
              <RefreshCw size={20} className="animate-spin mr-2" />
              音楽を生成中...
            </>
          ) : (
            <>
              <Music size={20} className="mr-2" />
              音楽を生成する
            </>
          )}
        </button>
      </div>

      {generatedMusic && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-4">
            <div className="bg-green-100 rounded-full p-2 mr-3">
              <Check size={16} className="text-green-600" />
            </div>
            <h3 className="font-medium">音楽が生成されました</h3>
          </div>

          <div className="mb-4">
            <audio
              ref={audioRef}
              controls
              className="w-full"
              src={generatedMusic.music_url}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500">
              リクエストID: {generatedMusic.request_id}
            </div>
            <button
              onClick={downloadMusic}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <Download size={16} className="mr-1" />
              <span className="text-sm font-medium">ダウンロード</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MusicGenerator;